<template>
  <v-dialog
    v-model="dialogProxy"
    :max-width="showPreview ? 'calc(100% - 550px)' : 560"
    persistent
  >
    <v-card>
      <v-card-title class="headline d-flex align-center">
        <span>Proforma Settings</span>
        <v-spacer />

        <v-btn
          v-if="showPreview"
          icon
          @click="showPreview = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="proforma-dialog-layout">
          <div class="proforma-form-pane">
            <v-form ref="proformaForm" lazy-validation>
              <div class="pa-6">
                <v-select
                  v-model="localForm.type"
                  :items="typeItems"
                  label="Type *"
                  outlined
                  dense
                  :rules="typeRules"
                  :disabled="localForm.include_order_items"
                />

                <v-switch
                  v-model="localForm.include_order_items"
                  label="Show all Sales Order items"
                  :hint="hasMixedItemVat
                    ? 'Summary mode creates one Előleg/Adminisztrációs díj row per VAT rate. Itemized mode shows the Sales Order product names.'
                    : 'When enabled, item names and units follow the Sales Order language.'"
                  persistent-hint
                  inset
                  class="mt-0 mb-4"
                />

                <v-row dense class="mt-2">
                  <v-col cols="12">
                    <v-radio-group
                      v-model="localForm.mode"
                      label="Calc. mode *"
                      class="mt-0"
                      hide-details="auto"
                      row
                      :rules="modeRules"
                    >
                      <v-radio label="Percent" value="percent" />
                      <v-radio label="Amount" value="value" />
                    </v-radio-group>
                  </v-col>

                  <v-col cols="12">
                    <v-text-field
                      v-if="localForm.mode === 'percent'"
                      v-model.number="localForm.percent"
                      label="Percent *"
                      type="number"
                      suffix="%"
                      min="0"
                      max="100"
                      outlined
                      dense
                      :rules="percentRules"
                    />

                    <v-text-field
                      v-else-if="localForm.mode === 'value'"
                      v-model.number="localForm.value"
                      label="Amount *"
                      type="number"
                      :suffix="computeSuffix"
                      min="0"
                      :max="invoiceDatas?.total_net ?? null"
                      outlined
                      dense
                      :hint="invoiceDatas ? 'Minimum: 0 - Maximum: ' + invoiceDatas.total_net : ''"
                      :rules="valueRules"
                    />
                  </v-col>
                </v-row>

                <v-alert
                  v-if="hasMixedItemVat"
                  dense
                  outlined
                  type="info"
                  class="mb-4"
                >
                  This Sales Order contains multiple VAT rates ({{ itemVatSummary }}). In summary mode, one identically named {{ localForm.type || 'advance' }} row is created for each VAT rate. Enable itemized mode only when the product names should appear on the document.
                </v-alert>

                <v-alert
                  v-if="localForm.include_order_items"
                  dense
                  outlined
                  type="info"
                  class="mb-4"
                >
                  The selected amount will be distributed proportionally between all Sales Order items. Item names use the Sales Order language and each item keeps its own VAT rate<span v-if="itemVatSummary"> ({{ itemVatSummary }})</span>.
                </v-alert>

                <v-combobox
                  v-if="!localForm.include_order_items && !hasMixedItemVat"
                  v-model="localForm.vat"
                  :items="vatItems"
                  label="Tax rate *"
                  outlined
                  dense
                  clearable
                  :rules="vatRules"
                  @change="handleVatChange"
                />

                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="dateMenus.fulfillmentDate"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="localForm.fulfillmentDate"
                          label="Date of completion"
                          outlined
                          dense
                          hint="Optional"
                          persistent-hint
                          readonly
                          clearable
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="localForm.fulfillmentDate = null"
                        />
                      </template>

                      <v-date-picker
                        v-model="localForm.fulfillmentDate"
                        no-title
                        @input="dateMenus.fulfillmentDate = false"
                      />
                    </v-menu>

                    <div class="d-flex flex-column mt-2 mb-2">
                      <div>Add days:</div>
                      <div class="d-flex flex-wrap">
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 0)">Today</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 1)">+1</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 2)">+2</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 3)">+3</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 5)">+5</v-btn>
                        <v-btn text @click="setTodayPlusDays('fulfillmentDate', 8)">+8</v-btn>
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="dateMenus.paymentDeadline"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="localForm.paymentDeadline"
                          label="Payment due date"
                          outlined
                          dense
                          hint="Optional"
                          persistent-hint
                          readonly
                          clearable
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="localForm.paymentDeadline = null"
                        />
                      </template>

                      <v-date-picker
                        v-model="localForm.paymentDeadline"
                        no-title
                        @input="dateMenus.paymentDeadline = false"
                      />
                    </v-menu>

                    <div class="d-flex flex-column mt-2 mb-2">
                      <div>Add days:</div>
                      <div class="d-flex flex-wrap">
                        <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 0)">Today</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 1)">+1</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 2)">+2</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 3)">+3</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('paymentDeadline', 5)">+5</v-btn>
                        <v-btn text @click="setTodayPlusDays('paymentDeadline', 8)">+8</v-btn>
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <v-textarea
                  v-model="localForm.note"
                  label="Comment"
                  outlined
                  dense
                  auto-grow
                  rows="3"
                  hint="Optional"
                  persistent-hint
                />
              </div>
            </v-form>
          </div>

          <v-expand-x-transition>
            <div v-if="showPreview" class="proforma-preview-pane">
              <v-divider vertical />

              <div class="pa-6 preview-pane-inner">
                <InvoicePreviewPanel
                  document-type="proforma"
                  :order="invoiceDatas"
                  :settings="previewSettings"
                  :seller="seller"
                  :currency="computeSuffix"
                />
              </div>
            </div>
          </v-expand-x-transition>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-6 py-4">
        <v-spacer />

        <v-btn
          v-if="invoiceDatas"
          color="primary"
          class="mr-2"
          @click="openPreview"
          :disabled="loading"
        >
          {{ !showPreview ? 'Preview proforma' : 'Close preview' }}
        </v-btn>

        <v-btn text @click="closeDialog"
          :disabled="loading"
        >
          Cancel
        </v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!isValid"
          @click="submit"
        >
          Generate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import InvoicePreviewPanel from './InvoicePreviewPanel.vue'
import Swal from "sweetalert2";


export default {
  name: 'ProformaDialog',

  components: {
    InvoicePreviewPanel,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    orderId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({
        id: null,
        type: null,
        mode: null,
        percent: null,
        value: null,
        vat: null,
        include_order_items: false,
        fulfillmentDate: null,
        paymentDeadline: null,
        note: null,
      }),
    },
    invoiceDatas: {
      type: Object,
      default: () => null,
    },
    seller: {
      type: Object,
      default: () => ({
        name: 'Kismetál Kft.',
        address: null,
        taxNumber: null,
        email: null,
        phone: null,
        bankAccount: null,
      }),
    },
  },

  data() {
    return {
      showPreview: false,
      vatDefaultApplied: false,
      vatChangedByUser: false,
      dateMenus: {
        fulfillmentDate: false,
        paymentDeadline: false,
        dueDate: false,
      },
      localForm: {
        id: null,
        type: null,
        mode: null,
        percent: null,
        value: null,
        vat: null,
        include_order_items: false,
        fulfillmentDate: null,
        paymentDeadline: null,
        note: null,
      },
      datas: null,
      vatItems: [
        'TAHK', 'TAM', 'AAM', 'EUT', 'EUKT', 'F.AFA', 'K.AFA', 'HO', 'EUE',
        'EUFADE', 'EUFAD37', 'ATK', 'NAM', 'EAM', 'KBAUK', 'KBAET',
        '0', '1', '2', '2.1', '3', '4', '4.8', '5', '5.5', '6', '7', '7.7',
        '8', '8.1', '9', '9.5', '10', '11', '12', '13', '13.5', '14', '15',
        '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '25.5',
        '26', '27',
      ],
      typeItems: [
        'Előleg',
        'Adminisztrációs díj',
      ],
    }
  },

  computed: {
    dialogProxy: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },

    itemVatCodes() {
      return this.collectItemVatCodes()
    },

    hasMixedItemVat() {
      return this.itemVatCodes.length > 1
    },

    itemVatSummary() {
      return this.itemVatCodes.join(', ')
    },

    isValid() {
      if (!this.localForm.type) return false
      if (!this.localForm.mode) return false
      if (!this.localForm.include_order_items && !this.hasMixedItemVat && !this.isValidVat(this.localForm.vat)) return false

      if (this.localForm.mode === 'percent') {
        return (
          this.localForm.percent !== null &&
          this.localForm.percent !== '' &&
          Number(this.localForm.percent) > 0 &&
          Number(this.localForm.percent) <= 100
        )
      }

      if (this.localForm.mode === 'value') {
        return (
          this.localForm.value !== null &&
          this.localForm.value !== '' &&
          Number(this.localForm.value) > 0
        )
      }

      return false
    },

    computeSuffix() {
      return this.datas?.priceList?.currency_id ?? 'HUF'
    },

    previewSettings() {
      return {
        id: this.orderId,
        type: this.localForm.type,
        mode: this.localForm.mode,
        percent: this.localForm.mode === 'percent'
          ? Number(this.localForm.percent)
          : null,
        value: this.localForm.mode === 'value'
          ? Number(this.localForm.value)
          : null,
        vat: (this.localForm.include_order_items || this.hasMixedItemVat) ? null : (this.localForm.vat || null),
        include_order_items: Boolean(this.localForm.include_order_items),
        fulfillmentDate: this.localForm.fulfillmentDate || null,
        paymentDeadline: this.localForm.paymentDeadline || null,
        note: this.localForm.note || null,
      }
    },

    maxAmount() {
      const total = this.invoiceDatas?.total_net;
      return total === null || total === undefined || total === ''
        ? null
        : Number(total);
    },

    typeRules() {
      return [
        v => !!v || 'You must select a type',
      ];
    },

    modeRules() {
      return [
        v => !!v || 'You must select a calculation mode',
      ];
    },

    percentRules() {
      return [
        v => this.localForm.mode !== 'percent' || v !== null && v !== '' || 'You must enter a percentage',
        v => this.localForm.mode !== 'percent' || !isNaN(Number(v)) || 'The percentage must be a number',
        v => this.localForm.mode !== 'percent' || Number(v) >= 0 || 'Minimum 0',
        v => this.localForm.mode !== 'percent' || Number(v) <= 100 || 'Maxmimum 100(%)',
      ];
    },

    valueRules() {
      return [
        v => this.localForm.mode !== 'value' || v !== null && v !== '' || 'You must enter an amount',
        v => this.localForm.mode !== 'value' || !isNaN(Number(v)) || 'The amount must be a number',
        v => this.localForm.mode !== 'value' || Number(v) >= 0 || 'Minimum 0',
        v =>
          this.localForm.mode !== 'value' ||
          this.maxAmount === null ||
          Number(v) <= this.maxAmount ||
          `Maximum ${this.maxAmount}`,
      ];
    },

    vatRules() {
      return [
        v => !!v || 'You must select one tax rate',
        v => this.vatItems.includes(String(v)) || 'You can select only the tax rate listed',
      ];
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          // Az automatikus ÁFA-alapérték minden dialógus-megnyitáskor egyszer futhat le.
          this.vatDefaultApplied = false
          this.vatChangedByUser = false
          this.syncForm()
        } else {
          this.showPreview = false
        }
      },
    },

    initialData: {
      deep: true,
      handler() {
        if (this.value && !this.vatChangedByUser) {
          // Az initialData későbbi frissülése ne törölje a már egyszer
          // automatikusan meghatározott termék-ÁFA kulcsot.
          this.syncForm({ preserveVat: this.vatDefaultApplied })
        }
      },
    },

    invoiceDatas: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.datas = val

          this.$nextTick(() => {
            this.enforceAdvanceModeRules()
            this.setInitialTax()
            this.calculateFAFA()
          })
        }
      },
    },

    'localForm.mode'(newMode) {
      if (newMode === 'percent') {
        this.localForm.value = null
      }

      if (newMode === 'value') {
        this.localForm.percent = null
      }
    },

    'localForm.include_order_items'(enabled) {
      if (!enabled) {
        this.setInitialTax()
      }

      this.calculateFAFA()
    },

    hasMixedItemVat() {
      this.enforceAdvanceModeRules()
    },

    'localForm.vat'(newVat) {
      const normalizedVat = String(newVat || '').trim().toUpperCase()
      const text = 'Az ÁFA törvény 142§ (1) bekezdés j. pontja alapján az adó fizetésére a vevő kötelezett.'

      if (
        normalizedVat === 'F.AFA'
        && (!this.localForm.note || !this.localForm.note.includes(text))
      ) {
        this.localForm.note = this.localForm.note
          ? this.localForm.note + '\n' + text
          : text
      }
    },
  },

  methods: {
    normalizeBoolean(value) {
      return value === true || value === 1 || value === '1' || value === 'true'
    },

    normalizeSalesPointIdentifier(value) {
      if (value === null || value === undefined || typeof value === 'object') return ''

      return String(value)
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/[^a-z0-9]/g, '')
    },

    isKismetalVaskerSalesPoint() {
      const salesPoint = this.invoiceDatas?.salesPoint || this.invoiceDatas?.sales_point || {}
      const candidates = [
        salesPoint,
        salesPoint?.name,
        salesPoint?.slug,
        salesPoint?.domain,
        salesPoint?.website,
        salesPoint?.website?.domain,
        salesPoint?.site?.domain,
        this.invoiceDatas?.sales_point,
        this.invoiceDatas?.sales_point_name,
        this.invoiceDatas?.salesPointName,
      ]

      return candidates.some(value =>
        this.normalizeSalesPointIdentifier(value).includes('kismetalvasker')
      )
    },

    isEurOrder() {
      const orderCurrency = String(
        this.invoiceDatas?.priceList?.currency_id
        || this.invoiceDatas?.price_list?.currency_id
        || this.invoiceDatas?.currency_id
        || ''
      ).trim().toUpperCase()

      if (orderCurrency === 'EUR') return true

      const items = this.getOrderItems()

      const itemCurrencies = items
        .map(item => String(
          item?.currency_id
          || item?.currency?.id
          || item?.priceList?.currency_id
          || item?.price_list?.currency_id
          || ''
        ).trim().toUpperCase())
        .filter(Boolean)

      return itemCurrencies.length > 0
        && itemCurrencies.every(currency => currency === 'EUR')
    },

    resolveAutomaticTaxDefault() {
      // EUR esetén az EUT az elsődleges automatikus alapérték.
      if (this.isEurOrder()) return 'EUT'

      if (
        this.isKismetalVaskerSalesPoint()
        && this.normalizeBoolean(this.invoiceDatas?.zero_tax)
      ) {
        return 'F.AFA'
      }

      return null
    },

    enforceAdvanceModeRules() {
      if (this.hasMixedItemVat) {
        // Vegyes ÁFA esetén az összesített mód is használható,
        // de a globális ÁFA-választó helyett a tétel ÁFA-bontása érvényesül.
        this.localForm.vat = null
      }
    },

    formatDateForInput(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },

    setTodayPlusDays(field, days) {
      const date = new Date()
      date.setHours(0, 0, 0, 0)
      date.setDate(date.getDate() + days)
      this.localForm[field] = this.formatDateForInput(date)
    },

    normalizeItemVat(value) {
      if (value === null || value === undefined || value === '') return null

      const normalized = String(value).trim().toUpperCase().replace(',', '.')
      return this.vatItems.includes(normalized) ? normalized : null
    },

    resolveStoredItemVat(item) {
      const candidates = [
        item?.vat_name,
        item?.vatName,
        item?.vat_value,
        item?.vatValue,
        item?.vat,
        item?.tax,
        item?.tax_rate,
        item?.taxRate,
        item?.item?.vat_name,
        item?.item?.vatName,
        item?.item?.vat_value,
        item?.item?.vatValue,
        item?.item?.vat,
        item?.item?.tax,
        item?.item?.tax_rate,
        item?.item?.taxRate,
        item?.price_list_item?.vat_name,
        item?.price_list_item?.vat_value,
        item?.priceListItem?.vat_name,
        item?.priceListItem?.vat_value,
      ]

      for (const candidate of candidates) {
        const normalized = this.normalizeItemVat(candidate)

        if (normalized) {
          return normalized
        }
      }

      return null
    },

    getOrderItems() {
      const candidates = [
        this.invoiceDatas?.items,
        this.invoiceDatas?.salesOrderItems,
        this.invoiceDatas?.sales_order_items,
        this.invoiceDatas?.order?.items,
        this.invoiceDatas?.order?.salesOrderItems,
        this.invoiceDatas?.order?.sales_order_items,
      ]

      // Ne az első üres tömböt válasszuk ki, ha egy másik kulcson
      // már ténylegesen megérkeztek a Sales Order tételei.
      return candidates.find(items => Array.isArray(items) && items.length > 0)
        || candidates.find(items => Array.isArray(items))
        || []
    },

    resolveItemVat(item) {
      /*
       * A kötelező rendelési szabályok megelőzik az esetleg korábbról
       * a tételen maradt 27%-os értéket.
       */
      const automaticTax = this.resolveAutomaticTaxDefault()

      if (automaticTax) return automaticTax

      const storedItemVat = this.resolveStoredItemVat(item)

      if (storedItemVat) return storedItemVat

      const zeroTax = this.normalizeBoolean(this.invoiceDatas?.zero_tax)
      const reverseTax = this.normalizeBoolean(this.invoiceDatas?.reverse_tax)
      const isService = this.normalizeBoolean(item?.service)
        || this.normalizeBoolean(item?.item?.service)

      if (zeroTax) return this.isEurOrder() ? 'EUT' : 'F.AFA'
      if (reverseTax && !isService) return 'F.AFA'

      return this.normalizeItemVat(this.invoiceDatas?.priceList?.vat_value)
        || this.normalizeItemVat(this.invoiceDatas?.price_list?.vat_value)
        || '27'
    },

    collectItemVatCodes() {
      return [...new Set(
        this.getOrderItems()
          .map(item => this.resolveItemVat(item))
          .filter(Boolean)
      )]
    },

    normalizeVat() {
      if (this.localForm.vat === null || this.localForm.vat === '') {
        this.localForm.vat = null
        return
      }

      this.localForm.vat = String(this.localForm.vat).trim().toUpperCase()
    },

    handleVatChange() {
      this.normalizeVat()
      this.vatChangedByUser = true
      this.vatDefaultApplied = true
      this.calculateFAFA()
    },

    isValidVat(vat) {
      if (vat === null || vat === '') return false
      const normalized = String(vat).trim().toUpperCase()
      return this.vatItems.includes(normalized)
    },

    syncForm({ preserveVat = false } = {}) {
      const resolvedMode = this.initialData?.mode ||
        (this.initialData?.value !== null && this.initialData?.value !== undefined
          ? 'value'
          : 'percent')

      const currentVat = preserveVat
        ? this.normalizeItemVat(this.localForm?.vat)
        : null

      this.localForm = {
        id: this.orderId,
        // Minden új kiállítás Előleg / összesített módból induljon.
        type: 'Előleg',
        mode: resolvedMode,
        percent: this.initialData?.percent ?? null,
        value: this.initialData?.value ?? null,
        vat: currentVat || this.initialData?.vat || null,
        include_order_items: false,
        fulfillmentDate: this.initialData?.fulfillmentDate || null,
        paymentDeadline: this.initialData?.paymentDeadline || null,
        note: this.initialData?.note || null,
      }

      this.normalizeVat()
      this.enforceAdvanceModeRules()
      //this.calculateWeight();
      this.calculateFAFA();

      if (!preserveVat) {
        this.setInitialTax()
      }
    },

    openPreview() {
      this.enforceAdvanceModeRules()

      if(this.showPreview){
        this.showPreview = false;
        return;
      }

      this.normalizeVat()

      const valid = this.$refs.proformaForm.validate()

      if (!valid) {
        return
      }

      this.showPreview = true
    },

    closeDialog() {
      this.showPreview = false
      this.syncForm()
      this.dialogProxy = false
    },

    submit() {
      this.enforceAdvanceModeRules()
      this.normalizeVat()


      const valid = this.$refs.proformaForm.validate()

      if (!valid) {
        return
      }

      const payload = {
        id: this.orderId,
        type: this.localForm.type,
        mode: this.localForm.mode,
        percent: this.localForm.mode === 'percent'
          ? Number(this.localForm.percent)
          : null,
        value: this.localForm.mode === 'value'
          ? Number(this.localForm.value)
          : null,
        vat: (this.localForm.include_order_items || this.hasMixedItemVat) ? null : (this.localForm.vat || null),
        include_order_items: Boolean(this.localForm.include_order_items),
        fulfillmentDate: this.localForm.fulfillmentDate || null,
        paymentDeadline: this.localForm.paymentDeadline || null,
        note: this.localForm.note || null,
      }

      Swal.fire({
          title: `<strong>ALERT</strong>`,
          icon: "warning",
          width: "600px",
          text: "Are you sure you want to create the this proforma? This action cannot be undone.",
          showCloseButton: true,
          focusConfirm: false,
          confirmButtonText: "Yes, create it!",
          confirmButtonColor: "#00c300",
          infoColor: "#e33354",
          showCancelButton: true,
      }).then((result) => {
          if (result.isConfirmed) {
              this.$emit('save', payload)
              //this.dialogProxy = false
          }
      });
    },

    calculateWeight() {
      if (!this.invoiceDatas || this.invoiceDatas.items.length <= 0) return;

      let sumWeight = 0;
      let weights = 0;

      this.invoiceDatas.items.forEach(element => {
        if (element.unit_weight !== null) {
          weights = Number(element.unit_weight) * Number(element.quantity) * Number(element.num_of_pcs || 0);
          sumWeight += weights;
        }
      });

      this.localForm.note = "Össztömeg: " + sumWeight.toFixed(1) + " kg\n";
    },

    calculateFAFA() {
      if (!this.invoiceDatas) return

      const text = 'Az ÁFA törvény 142§ (1) bekezdés j. pontja alapján az adó fizetésére a vevő kötelezett.'
      const usesReverseVat = (this.localForm.include_order_items || this.hasMixedItemVat)
        ? this.itemVatCodes.includes('F.AFA')
        : this.normalizeItemVat(this.localForm.vat) === 'F.AFA'

      if (usesReverseVat && (!this.localForm.note || !this.localForm.note.includes(text))) {
        this.localForm.note = this.localForm.note
          ? this.localForm.note + '\n' + text
          : text
      }
    },

    setInitialTax() {
      if (
        !this.invoiceDatas
        || this.vatDefaultApplied
        || this.vatChangedByUser
      ) {
        return
      }

      const items = this.getOrderItems()
      const automaticTax = this.resolveAutomaticTaxDefault()
      const itemVatCodes = this.collectItemVatCodes()
      const hasMixedVat = itemVatCodes.length > 1

      /*
       * Ne tekintsük beállítottnak addig, amíg a tételek még nem érkeztek meg.
       * Ez volt az oka annak, hogy a kezdeti 27% bent maradt.
       */
      if (items.length === 0 && !automaticTax) {
        return
      }

      if (this.localForm.include_order_items || hasMixedVat) {
        if (hasMixedVat) {
          this.localForm.vat = null
        }

        this.vatDefaultApplied = true
        this.calculateFAFA()
        return
      }

      // Elsődlegesen a Sales Order tételein ténylegesen tárolt,
      // egységes ÁFA-kulcsot használjuk (például 15%).
      this.localForm.vat = itemVatCodes[0]
        || automaticTax
        || this.normalizeItemVat(this.localForm.vat)
        || this.normalizeItemVat(this.invoiceDatas?.priceList?.vat_value)
        || this.normalizeItemVat(this.invoiceDatas?.price_list?.vat_value)
        || '27'

      // Innentől a felhasználó kézi választása az irányadó.
      this.vatDefaultApplied = true
      this.calculateFAFA()
    },

    limitValue(val) {
      console.log(val)
      const max = Number(this.invoiceDatas?.total_net ?? 0);
      const num = Number(val ?? 0);

      console.log('FORM '+Number(this.localForm.value));
      if(Number(this.localForm.value) > max){
        this.localForm.value = max;
      }else if(Number(this.localForm.value) <= 0){
        this.localForm.value = 0;
      }

      if (num > max) {
        this.localForm.value = max;
      } else if (num < 0) {
        this.localForm.value = 0;
      }
    }
  },
}
</script>
<style scoped>
.proforma-dialog-layout {
  display: flex;
  align-items: stretch;
  min-height: 620px;
}

.proforma-form-pane {
  flex: 0 0 560px;
  max-width: 560px;
}

.proforma-preview-pane {
  display: flex;
  flex: 1;
  min-width: 0;
  background: #fafafa;
}

.preview-pane-inner {
  flex: 1;
  min-width: 0;
  overflow: auto;
}
</style>